// 連続クリック時の処理を制御するための時間
import apiService from '@/member/services/apiService'

const processingInterval = 500

const getDefaultState = () => {
  return {
    selectedAreas: [],
    // 選択エリアの処理時刻
    // memo: 1度のクリックイベントで複数回処理されないようにするために使用する
    selectedAreaProcessedAt: {},
    simulationSetting: null,
    justSelectedAreaForestHierarchy: {},
    // FCS2024
    selectedMode: '',
    selectedTreeSpecies: [],
    selectedTreeAges: [],
    selectedOperationPlanned: [],
    // モニタリンググループ
    selectedGroup: null,
    selectedSpecies: null,
    monitoringAreaGroupsBySpecies: {},
    // モニタリングプロット
    representativePointList: [],
  }
}

const state = getDefaultState()

const mutations = {
  RESET_SELECTED_AREAS(state) {
    state.selectedAreas = []
  },
  RESET_MONITORING_AREA_GROUPS_BY_SPECIES(state) {
    state.monitoringAreaGroupsBySpecies = {}
  },
  RESET_REPRESENTATIVE_POINT_LIST(state) {
    state.representativePointList = []
  },
  SET_SELECTED_AREAS(state, payload) {
    state.selectedAreas = payload
  },
  SET_JUST_SELECTED_AREA_FOREST_HIERARCHY(state, payload) {
    state.justSelectedAreaForestHierarchy = {
      operation: payload.operation,
      forest_group: parseInt(payload.forest_group),
      small_forest_group: payload.small_forest_group,
      branch_group: parseInt(payload.branch_group),
      compensate_group: parseInt(payload.compensate_group),
      area_group: parseInt(payload.area_group),
      tree_species: parseInt(payload.tree_species),
    }
  },
  /**
   * 選択エリアを設定する
   * @param state
   * @param payload {Object} 選択エリア情報
   *  'operation' キーが存在する場合は、選択エリアの追加・削除を行う
   *  'operation' = 'add' : 選択エリアを追加する
   *  'operation' = 'remove' : 選択エリアを削除する
   * @constructor
   */
  SET_SELECTED_AREA(state, payload) {
    // 前回の処理から一定時間経過していない場合は処理しない
    const now = new Date()
    const nowTimestamp = now.getTime()
    const processedAtKey = `${payload.forest_group}_${payload.small_forest_group}_${payload.branch_group}_${payload.compensate_group}_${payload.area_group}_${payload.tree_species}`
    const lastProcessedAt = state.selectedAreaProcessedAt[processedAtKey]

    if (lastProcessedAt && nowTimestamp - lastProcessedAt < processingInterval) {
      return
    }
    state.selectedAreaProcessedAt[processedAtKey] = nowTimestamp

    // 選択済判定
    let selectedAreasIndex = null
    const isAlreadySelected = state.selectedAreas.some((selectedArea, i) => {
      const result =
        parseInt(selectedArea.forest_group) === parseInt(payload.forest_group) &&
        selectedArea.small_forest_group === payload.small_forest_group &&
        parseInt(selectedArea.branch_group) === parseInt(payload.branch_group) &&
        parseInt(selectedArea.compensate_group) === parseInt(payload.compensate_group) &&
        parseInt(selectedArea.area_group) === parseInt(payload.area_group) &&
        parseInt(selectedArea.tree_species) === parseInt(payload.tree_species)

      if (result) {
        selectedAreasIndex = i
      }

      return result
    })

    // 同じ林小班で異なるエリアグループが既に選択されているかチェック
    const hasDifferentAreaGroup = state.selectedAreas.some(selectedArea => {
      return (
        parseInt(selectedArea.forest_group) === parseInt(payload.forest_group) &&
        selectedArea.small_forest_group === payload.small_forest_group &&
        parseInt(selectedArea.branch_group) === parseInt(payload.branch_group) &&
        parseInt(selectedArea.compensate_group) === parseInt(payload.compensate_group) &&
        parseInt(selectedArea.area_group) !== parseInt(payload.area_group) &&
        parseInt(selectedArea.tree_species) === parseInt(payload.tree_species)
      )
    })

    // 異なるエリアグループが存在する場合は処理を中断
    if (hasDifferentAreaGroup) {
      return
    }

    // 外部からの操作を受け付ける場合
    if ('operation' in payload) {
      if (payload.operation === 'add' && !isAlreadySelected) {
        delete payload.operation
        state.selectedAreas.push(payload)
      } else if (payload.operation === 'remove' && isAlreadySelected) {
        state.selectedAreas.splice(selectedAreasIndex, 1)
      }
      return
    }

    if (!isAlreadySelected) {
      // 選択済の場合 エリアに追加する
      state.selectedAreas.push(payload)
    } else {
      // 選択済の場合 エリアを解除する
      state.selectedAreas.splice(selectedAreasIndex, 1)
    }
  },
  SET_SELECTED_AREAS_BY_SETTING(state, payload) {
    state.selectedAreas = []

    // 林小班で重複しないようにする
    payload.forEach(area => {
      const selectedArea = state.selectedAreas.find(selectedArea => {
        return (
          selectedArea['forest_group'] === area['forest_group'] &&
          selectedArea['small_forest_group'] === area['small_forest_group'] &&
          selectedArea['branch_group'] === area['branch_group'] &&
          selectedArea['compensate_group'] === area['compensate_group']
        )
      })

      if (!selectedArea) {
        state.selectedAreas.push(area)
      }
    })
  },
  SET_SIMULATION_SETTING(state, payload) {
    state.simulationSetting = payload
  },
  SET_SELECTED_MODE(state, payload) {
    state.selectedMode = payload
  },
  SET_TREE_SPECIES(state, payload) {
    state.selectedTreeSpecies = payload
  },
  SET_SELECTED_TREE_AGES(state, payload) {
    state.selectedTreeAges = payload
  },
  SET_SELECTED_OPERATION_PLANNED(state, payload) {
    state.selectedOperationPlanned = payload
  },
  SET_SELECTED_GROUP(state, payload) {
    state.selectedGroup = payload
  },
  SET_SELECTED_SPECIES(state, payload) {
    state.selectedSpecies = payload
  },
  SET_MONITORING_AREA_GROUPS_BY_SPECIES(state, payload) {
    state.monitoringAreaGroupsBySpecies = payload
  },
  SET_REPRESENTATIVE_POINT_LIST(state, payload) {
    // 配列内に同一のオブジェクトが存在するかチェック
    const isDuplicate = state.representativePointList.some(
      point =>
        point.forest_group === payload.forest_group &&
        point.small_forest_group === payload.small_forest_group &&
        point.branch_group === payload.branch_group &&
        point.compensate_group === payload.compensate_group &&
        point.area_group === payload.area_group &&
        point.tree_species === payload.tree_species
    )

    if (!isDuplicate) {
      // 同じarea_groupが存在するインデックスを探す
      const existingIndex = state.representativePointList.findIndex(point => point.area_group === payload.area_group)

      if (existingIndex !== -1) {
        // 同じarea_groupが存在する場合は上書き
        state.representativePointList[existingIndex] = payload
      } else {
        // 存在しない場合は新規追加
        state.representativePointList.push(payload)
      }
    }
  },
}

const actions = {
  async resetSelectedAreas({ commit }, payload) {
    commit('RESET_SELECTED_AREAS', payload)
  },
  async setSelectedAreas({ commit }, payload) {
    commit('SET_SELECTED_AREAS', payload)
  },
  // 直前に選ばれたエリアの森林情報をセットする
  async setJustSelectedAreaForestHierarchy({ commit }, payload) {
    commit('SET_JUST_SELECTED_AREA_FOREST_HIERARCHY', payload)
  },
  async setSelectedArea({ commit }, payload) {
    commit('SET_SELECTED_AREA', payload)
  },
  async setSelectedAreasBySetting({ commit }, payload) {
    commit('SET_SELECTED_AREAS_BY_SETTING', payload)
  },
  async setSimulationSetting({ commit }, payload) {
    commit('SET_SIMULATION_SETTING', payload)
  },
  async getSpatialFileSettings({ commit }, payload) {
    const apiResult = await apiService.get(route('member.fcs.spatial-file.get-spatial-file-settings'), payload)
    if (apiResult && apiResult.data) {
      return apiResult.data
    }
  },
  async getTileSet({ commit }, payload) {
    const apiResult = await apiService.get(route('member.fcs.mapbox-tileset.get-mapbox-tileset'), payload)
    if (apiResult && apiResult.data) {
      return apiResult.data
    }
  },
  async setSelectedMode({ commit }, payload) {
    commit('SET_SELECTED_MODE', payload)
  },
  async setTreeSpecies({ commit }, payload) {
    commit('SET_TREE_SPECIES', payload)
  },
  async setSelectedAges({ commit }, payload) {
    commit('SET_SELECTED_TREE_AGES', payload)
  },
  async setOperationPlanned({ commit }, payload) {
    commit('SET_SELECTED_OPERATION_PLANNED', payload)
  },
  async setSelectedGroup({ commit }, payload) {
    commit('SET_SELECTED_GROUP', payload)
  },
  async setSelectedSpecies({ commit }, payload) {
    commit('SET_SELECTED_SPECIES', payload)
  },
  async setMonitoringAreaGroupsBySpecies({ commit }, payload) {
    commit('SET_MONITORING_AREA_GROUPS_BY_SPECIES', payload)
  },
  async setRepresentativePointList({ commit }, payload) {
    commit('SET_REPRESENTATIVE_POINT_LIST', payload)
  },
  async resetMonitoringMapData({ commit }, payload) {
    commit('RESET_SELECTED_AREAS', payload)
    commit('RESET_MONITORING_AREA_GROUPS_BY_SPECIES', payload)
    commit('RESET_REPRESENTATIVE_POINT_LIST', payload)
  },
}

const getters = {
  justSelectedAreaForestHierarchy(state) {
    return state.justSelectedAreaForestHierarchy ?? null
  },
  selectedAreas(state) {
    return state.selectedAreas ?? []
  },
  simulationSetting(state) {
    return state.simulationSetting ?? null
  },
  selectedMode(state) {
    return state.selectedMode ?? null
  },
  selectedTreeSpecies(state) {
    return state.selectedTreeSpecies ?? null
  },
  selectedTreeAges(state) {
    return state.selectedTreeAges ?? null
  },
  selectedOperationPlanned(state) {
    return state.selectedOperationPlanned ?? null
  },
  selectedGroup(state) {
    return state.selectedGroup ?? null
  },
  selectedSpecies(state) {
    return state.selectedSpecies ?? null
  },
  monitoringAreaGroupsBySpecies(state) {
    return state.monitoringAreaGroupsBySpecies ?? {}
  },
  representativePointList(state) {
    return state.representativePointList ?? []
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
